import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Type, Eye, Search, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Footer } from "../components/Footer";
import { CompactHeader } from "../components/CompactHeader";
import BackLoader from "../../components/backloader";
import * as Constants from "../../constants";

// Cause list results. Reads ?q=&field=&type=&date= (also the older ?filter= from the Daily/Advance/Supplementary pages)
// and searches on the server (cms/cause-list-search). Cards on phones, a table on wider screens.

const FIELDS = [
  { id: "all", label: "Everything" },
  { id: "suit", label: "Suit Number" },
  { id: "party", label: "Party Name" },
  { id: "judge", label: "Judge" },
  { id: "court-room", label: "Court Room" },
  { id: "status", label: "Status" },
];

const TYPES = [
  { id: "search", label: "Any date" },
  { id: "daily", label: "Daily list" },
  { id: "advance", label: "Advance list" },
  { id: "supplementary", label: "Supplementary list" },
  { id: "archives", label: "Archives" },
];

const PAGE_SIZE = 25;

// the list pages send filter ids such as "Party-Name" / "court-room"
const normalizeField = (f) => {
  const v = (f || "").toLowerCase();
  if (v.startsWith("party")) return "party";
  if (v.startsWith("court")) return "court-room";
  if (v.startsWith("judge")) return "judge";
  if (v.startsWith("status")) return "status";
  if (v.startsWith("suit")) return "suit";
  return "all";
};

const todayYmd = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export default function SearchResultsPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [textSize, setTextSize] = useState("normal");
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [error, setError] = useState("");

  const urlQ = searchParams.get("q") || "";
  const urlType = (searchParams.get("type") || "search").toLowerCase();
  const urlField = normalizeField(searchParams.get("field") || searchParams.get("filter"));
  const urlDate = searchParams.get("date") || (urlType === "search" ? "" : todayYmd());
  const urlValue = searchParams.get("value") || "";
  const pickable = ["judge", "court-room", "status"].includes(urlField);
  const [options, setOptions] = useState(null);

  // the refine form (starts from the URL)
  const [form, setForm] = useState({ q: urlQ, type: urlType, field: urlField, date: urlDate });
  useEffect(() => {
    setForm({ q: urlQ, type: urlType, field: urlField, date: urlDate });
    setPage(0);
    load(urlQ, urlType, urlField, urlDate, 0);
    // "Search by Judge / Court Room / Status": offer the values on this list to pick from
    setOptions(null);
    if (pickable) loadOptions(urlType, urlField, urlDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const loadOptions = async (type, field, date) => {
    try {
      const params = new URLSearchParams({ type, field });
      if (date) params.set("date", date);
      const resp = await fetch(`${Constants.apiUrl}/cms/cause-list-options?${params.toString()}`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });
      const data = await resp.json();
      if (data.code === 0) setOptions(data.options || []);
    } catch (e) {
      console.error(e);
    }
  };

  const pickValue = (v) => {
    const params = new URLSearchParams(searchParams);
    params.delete("filter");
    params.set("field", urlField);
    if (v) params.set("value", v);
    else params.delete("value");
    params.delete("q");
    navigate(`/cause-list/search?${params.toString()}`);
  };

  const load = async (q, type, field, date, p) => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams({ q: q || "", type, field, page: String(p), size: String(PAGE_SIZE) });
      if (date) params.set("date", date);
      if (urlValue) params.set("value", urlValue);
      const resp = await fetch(`${Constants.apiUrl}/cms/cause-list-search?${params.toString()}`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });
      const data = await resp.json();
      if (data.code === 0) {
        setItems(data.items || []);
        setTotal(data.totalCount || 0);
      } else {
        setItems([]);
        setTotal(0);
        setError("The cause list could not be loaded. Please try again.");
      }
    } catch (e) {
      console.error(e);
      setItems([]);
      setTotal(0);
      setError("The cause list could not be loaded. Please try again.");
    }
    setLoading(false);
  };

  const onSubmit = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (form.q.trim()) params.set("q", form.q.trim());
    params.set("type", form.type);
    if (form.field !== "all") params.set("field", form.field);
    if (form.date) params.set("date", form.date);
    navigate(`/cause-list/search?${params.toString()}`);
  };

  const goPage = (p) => {
    setPage(p);
    load(urlQ, urlType, urlField, urlDate, p);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const getFontSizeClass = () => {
    if (textSize === "large") return "text-lg";
    if (textSize === "small") return "text-sm";
    return "text-base";
  };

  const getStatusColor = (status) => {
    const s = (status || "").toLowerCase();
    if (s.includes("hearing")) return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200";
    if (s.includes("judgment")) return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200";
    if (s.includes("mention")) return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200";
    if (s.includes("adjournment")) return "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200";
    return "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200";
  };

  const fmtDate = (d) => (d ? Constants.momentDate(d).format("ddd, D MMM YYYY") : "-");
  const title = TYPES.find((t) => t.id === urlType)?.label || "Search";
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const caseLink = (r) => `/online-search/results?q=${encodeURIComponent(r.suitNumber || "")}`;

  // The landing pages use a pre-built Tailwind file: only classes already in it work, so the
  // responsive bits (cards on phones, the refine form grid) use the small stylesheet below.
  const LOCAL_CSS = `
    .cl-form{display:grid;grid-template-columns:1fr;gap:12px;align-items:end;margin-top:20px;text-align:left}
    @media(min-width:640px){.cl-form{grid-template-columns:1fr 1fr}}
    @media(min-width:1024px){.cl-form{grid-template-columns:4fr 2fr 2fr 2fr 2fr}}
    .cl-form label{display:block;font-size:12px;font-weight:600;margin-bottom:4px;color:#374151}
    .cl-form select,.cl-form input[type=date]{width:100%;height:44px;border-radius:6px;border:1px solid #d1d5db;background:#fff;color:#111827;padding:0 10px}
    .cl-chips{display:flex;flex-wrap:wrap;gap:8px;max-height:224px;overflow-y:auto;justify-content:center}
    .cl-chip{border-radius:9999px;border:1px solid #d1d5db;padding:6px 12px;font-size:14px;background:#fff;color:#1f2937;cursor:pointer}
    .cl-chip:hover{border-color:#155dfc}
    .cl-chip.on{background:#155dfc;color:#fff;border-color:#155dfc}
    .cl-cards{display:none}
    @media(max-width:767px){.cl-cards{display:block}.cl-table{display:none}}
    .cl-card{border:1px solid #e5e7eb;border-radius:12px;background:#fff;padding:16px;margin-bottom:12px;box-shadow:0 1px 2px rgba(0,0,0,.05)}
    .cl-card .meta{display:grid;grid-template-columns:1fr 1fr;gap:4px 12px;margin-top:12px;font-size:14px;color:#4b5563}
    .cl-card .meta .full{grid-column:1 / -1}
    .cl-card .lbl{display:block;font-size:12px;opacity:.7}
    .cl-blue{color:#155dfc}
    .cl-wrap{white-space:normal;word-break:break-word}
    @media(max-width:767px){.cl-hero{padding-top:40px !important;padding-bottom:40px !important}}
  `;

  return (
    <div className="min-h-screen bg-white dark:bg-slate-900">
      <style>{LOCAL_CSS}</style>
      <CompactHeader />
      {/* Accessibility Controls Bar */}
      <div className="bg-[#1E293B] text-white py-3 px-4 sticky top-[100px] z-40">
        <div className="max-w-7xl mx-auto flex justify-end items-center">
          <div className="flex items-center gap-3">
            <Type className="w-5 h-5" aria-hidden="true" />
            <div className="flex gap-2" role="group" aria-label="Text size controls">
              {["small", "normal", "large"].map((size) => (
                <button
                  key={size}
                  onClick={() => setTextSize(size)}
                  className={`px-3 py-2 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-white/50 ${
                    textSize === size ? "bg-white text-[#1E293B]" : "bg-white/10 hover:bg-white/20"
                  }`}
                  aria-label={`${size} text size`}
                  aria-pressed={textSize === size}
                >
                  {size === "small" ? "A-" : size === "normal" ? "A" : "A+"}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Back Button Section */}
      <div className="bg-gray-50 dark:bg-slate-800 py-4 px-4 border-b border-gray-200 dark:border-slate-700">
        <div className="max-w-7xl mx-auto">
          <Link
            to="/CauseLists"
            className="inline-flex items-center gap-2 text-[#155dfc] hover:text-[#6B1515] transition-colors focus:outline-none focus:ring-2 focus:ring-[#155dfc]/50 rounded-lg px-3 py-2"
            aria-label="Go back to Cause List"
          >
            <ArrowLeft className="w-5 h-5" aria-hidden="true" />
            <span className={getFontSizeClass()}>Back to Cause List</span>
          </Link>
        </div>
      </div>

      {/* Header Section with Gradient */}
      <section className="cl-hero relative py-24 px-4 bg-hero-light-image dark:bg-hero-dark">
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-6">
            <h1 className="text-4xl md:text-6xl font-bold mb-3 text-gray-900 drop-shadow-sm">{title}</h1>
            <p className={`${getFontSizeClass()} text-gray-800 drop-shadow-sm`}>
              {urlValue ? (
                <>
                  {FIELDS.find((f) => f.id === urlField)?.label}: <span className="font-semibold">{urlValue}</span>
                </>
              ) : urlQ ? (
                <>
                  Showing results for: <span className="font-semibold">"{urlQ}"</span>
                  {urlField !== "all" && <> in {FIELDS.find((f) => f.id === urlField)?.label}</>}
                </>
              ) : (
                "All listed cases"
              )}
              {urlDate && (
                <>
                  {" "}
                  · {urlType === "advance" ? "from" : urlType === "archives" ? "before" : "on"} {fmtDate(urlDate)}
                </>
              )}
            </p>
            <p className={`${getFontSizeClass()} text-gray-700 mt-2 drop-shadow-sm`}>{total.toLocaleString()} case(s) found</p>
          </div>

          {pickable && options && (
            <div className="max-w-4xl mx-auto text-center" style={{ marginBottom: 16 }}>
              <div className="text-sm font-semibold text-gray-800" style={{ marginBottom: 8 }}>
                {urlValue ? "Showing one " : "Choose a "}
                {(FIELDS.find((f) => f.id === urlField)?.label || "").toLowerCase()} on this list
                {urlValue && (
                  <button type="button" onClick={() => pickValue("")} className="cl-blue" style={{ marginLeft: 8, textDecoration: "underline" }}>
                    show all
                  </button>
                )}
              </div>
              {options.length === 0 ? (
                <div className="text-sm text-gray-700">Nothing is listed for this date.</div>
              ) : (
                <div className="cl-chips">
                  {options.map((o) => (
                    <button key={o.value} type="button" onClick={() => pickValue(o.value)} className={`cl-chip ${urlValue === o.value ? "on" : ""}`}>
                      {o.value} <span style={{ opacity: 0.7 }}>({o.count})</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <form onSubmit={onSubmit} className="cl-form max-w-7xl mx-auto">
            <div>
              <label>Search</label>
              <Input
                value={form.q}
                onChange={(e) => setForm({ ...form, q: e.target.value })}
                placeholder="Suit number, party, judge, court room..."
                className="w-full rounded-xl bg-white text-gray-900 border-0"
                style={{ height: 44 }}
                aria-label="Search text"
              />
            </div>
            <div>
              <label>Search in</label>
              <select value={form.field} onChange={(e) => setForm({ ...form, field: e.target.value })}>
                {FIELDS.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label>List</label>
              <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value, date: e.target.value === "search" ? "" : form.date || todayYmd() })}>
                {TYPES.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label>{form.type === "advance" ? "From date" : form.type === "archives" ? "Before date" : "Date"}</label>
              <div style={{ display: "flex", alignItems: "center" }}>
                <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
                {form.date && form.type === "search" && (
                  <button type="button" style={{ padding: "0 8px", color: "#6b7280" }} onClick={() => setForm({ ...form, date: "" })} aria-label="Clear date">
                    ✕
                  </button>
                )}
              </div>
            </div>
            <div>
              <Button type="submit" className="w-full text-white rounded-xl" style={{ height: 44 }}>
                <Search className="mr-2 w-5 h-5" /> Search
              </Button>
            </div>
          </form>
        </div>
      </section>

      {/* Results */}
      <section className="py-12 px-4 bg-white dark:bg-slate-900">
        <div className="max-w-7xl mx-auto">
          {error && (
            <div style={{ marginBottom: 16, border: "1px solid #fecaca", background: "#fef2f2", color: "#b91c1c", padding: "12px 16px", borderRadius: 8 }}>{error}</div>
          )}

          {/* phones: cards */}
          <div className="cl-cards">
            {items.map((r) => (
              <div key={r.id} className="cl-card">
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                  <div className={`${getFontSizeClass()} font-semibold cl-blue cl-wrap`}>{r.suitNumber}</div>
                  {r.status && <Badge className={`${getStatusColor(r.status)} cl-wrap`}>{r.status}</Badge>}
                </div>
                <div className={`${getFontSizeClass()} text-gray-900 cl-wrap`} style={{ marginTop: 8 }}>
                  {r.parties}
                </div>
                <div className="meta">
                  <div>
                    <span className="lbl">Date</span>
                    {fmtDate(r.date)}
                  </div>
                  <div>
                    <span className="lbl">Court room</span>
                    {r.courtRoomNo || "-"}
                  </div>
                  <div className="full">
                    <span className="lbl">Judge</span>
                    {r.judge || "-"}
                  </div>
                  {r.caseClassification && (
                    <div className="full">
                      <span className="lbl">Classification</span>
                      {r.caseClassification}
                    </div>
                  )}
                </div>
                {r.suitNumber && (
                  <Link to={caseLink(r)} className="inline-flex items-center gap-1 text-sm font-medium cl-blue" style={{ marginTop: 12 }}>
                    <Eye className="w-4 h-4" /> View case
                  </Link>
                )}
              </div>
            ))}
          </div>

          {/* tablets and up: the table */}
          {items.length > 0 && (
            <div className="cl-table bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-gray-200 dark:border-slate-700 overflow-hidden">
              <div className="overflow-x-auto">
                <Table className="w-full table-fixed">
                  <colgroup>
                    <col style={{ width: "11%" }} />
                    <col style={{ width: "34%" }} />
                    <col style={{ width: "11%" }} />
                    <col style={{ width: "14%" }} />
                    <col style={{ width: "10%" }} />
                    <col style={{ width: "7%" }} />
                    <col style={{ width: "8%" }} />
                    <col style={{ width: "5%" }} />
                  </colgroup>
                  <TableHeader>
                    <TableRow className="bg-[#155dfc] hover:bg-[#155dfc]">
                      {["SUIT NUMBER", "PARTIES", "CLASSIFICATION", "JUDGE NAME", "DATE", "COURT ROOM", "STATUS", "ACTIONS"].map((h) => (
                        <TableHead key={h} className={`table-head ${getFontSizeClass()}`}>
                          {h}
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map((r, index) => (
                      <TableRow
                        key={r.id}
                        className={`${index % 2 === 0 ? "bg-gray-50 dark:bg-slate-700" : "bg-white dark:bg-slate-800"} hover:bg-blue-50 dark:hover:bg-slate-600 transition-colors`}
                      >
                        <TableCell className={`${getFontSizeClass()} font-medium cl-blue cl-wrap`}>{r.suitNumber}</TableCell>
                        <TableCell className={`${getFontSizeClass()} cl-wrap`}>{r.parties}</TableCell>
                        <TableCell className={`${getFontSizeClass()} cl-wrap`}>
                          {r.caseClassification && (
                            <Badge variant="outline" className="cl-wrap">
                              {r.caseClassification}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className={`${getFontSizeClass()} cl-wrap`}>{r.judge}</TableCell>
                        <TableCell className={`${getFontSizeClass()} cl-wrap`}>{fmtDate(r.date)}</TableCell>
                        <TableCell className={`${getFontSizeClass()} text-center cl-wrap`}>{r.courtRoomNo}</TableCell>
                        <TableCell className="cl-wrap">
                          {r.status && <Badge className={`${getStatusColor(r.status)} cl-wrap`}>{r.status}</Badge>}
                        </TableCell>
                        <TableCell className="text-center">
                          {r.suitNumber && (
                            <Link to={caseLink(r)} className="inline-flex items-center justify-center rounded-md p-2 cl-blue" aria-label={`View case ${r.suitNumber}`} title="View case">
                              <Eye className="w-4 h-4" />
                            </Link>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}

          {/* paging */}
          {total > PAGE_SIZE && (
            <div className="flex items-center justify-center gap-3 mt-6">
              <Button variant="outline" disabled={page === 0} onClick={() => goPage(page - 1)} aria-label="Previous page">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm text-gray-700 dark:text-gray-300">
                Page {page + 1} of {pages}
              </span>
              <Button variant="outline" disabled={page + 1 >= pages} onClick={() => goPage(page + 1)} aria-label="Next page">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          )}

          {!loading && !error && items.length === 0 && (
            <div className="text-center py-16">
              <div className="bg-gray-50 dark:bg-slate-800 rounded-xl p-12 max-w-2xl mx-auto border-2 border-gray-200 dark:border-slate-700">
                <h3 className={`${getFontSizeClass()} text-gray-900 dark:text-white mb-4`}>No Results Found</h3>
                <p className={`${getFontSizeClass()} text-gray-600 dark:text-gray-400 mb-6`}>
                  {urlQ ? <>We couldn't find any cases matching "{urlQ}".</> : <>No cases are listed for this selection.</>}
                </p>
                <Link to="/CauseLists">
                  <Button className="bg-[#155dfc] hover:bg-[#6B1515]">Return to Cause List</Button>
                </Link>
              </div>
            </div>
          )}
        </div>
      </section>
      <Footer />
      <BackLoader open={loading} />
    </div>
  );
}
